import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import ViewWeekOutlinedIcon from "@mui/icons-material/ViewWeekOutlined";
import {
  IconButton,
  TableRow as MuiTableRow,
  Paper,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  Typography
} from "@mui/material";
import { BinDeleteIcon, CustomizeTable } from "assets/material-custom-icons";
import { ProgressOverlay, RoleComponentGuard } from "components";
import { Menu } from "components/popups";
import { defaultDocumentReportQueries } from "constants/documents";
import { useActionHook, useModals, usePrompt } from "hooks";
import { debounce } from "lodash";
import { useReportsActions } from "store";
import { ClientDealTableRow, DocumentReportQueries, DocumentSectionTable, RowBy } from "store/types";
import { normalizeGroupByCharacteristic } from "utils";
import syncReportTableColumn from "utils/sync-report-table-column";

import { RoleName } from "../../../../constants";
import { useContentContext } from "../../Content/content-context";
import { EditQueries } from "../../Report-sections-forms/EditQueries";
import { EditTable } from "../../Report-sections-forms/EditTable";
import { ReportQueries } from "../ReportQueries";
import { TableRow } from "./TableRow";

interface TableSectionProps {
  section: DocumentSectionTable;
}

const maxHeight = 600;

const StyledTableHeadCell = styled(TableCell)({
  fontSize: "16px",
  fontWeight: 400,
  color: "#071F35",
  whiteSpace: "nowrap",
  borderBottom: "none",
  maxHeight: "40px"
});

export const TableSection: React.FC<TableSectionProps> = ({ section }) => {
  const { settings, queries } = section.report_data;

  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [isAllExpanded, setIsAllExpanded] = useState(false);
  const [tableContainerHeight, setTableContainerHeight] = useState<number>(section.parameters?.height || 300);

  // Add refs for resize tracking
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const isResizingRef = useRef(false);
  const initialHeightRef = useRef<number>(300);

  const openPrompt = usePrompt();
  const { getReportTableAction } = useReportsActions();
  const { report_queries: globalQueries, tablesQueue, deleteSection, updateSection } = useContentContext();
  const { deleteModal } = useModals();

  const {
    data: tables,
    action: getTableData,
    loading: getTableDataLoading
  } = useActionHook(getReportTableAction, {
    defaultLoadingState: true
  });

  const { action: updateSectionAction, loading: updateSectionLoading } = useActionHook(updateSection);

  const resultQueries: DocumentReportQueries = useMemo(() => {
    return {
      ...defaultDocumentReportQueries,
      ...(queries || globalQueries)
    };
  }, [queries, globalQueries]);

  useEffect(() => {
    tablesQueue.addRequest({
      func: getTableData,
      args: [
        {
          dateRange: resultQueries.dateRange,
          currency: resultQueries.currency,
          exchangeMethod: resultQueries.exchangeMethod,
          filters: resultQueries.filters,

          measures: settings.columns.measures,
          calculatedFields: settings.columns.calcFields,
          characteristics: settings.columns.characteristics,
          columns: syncReportTableColumn(settings.columns, []),

          groupBy: settings.group_by.map(normalizeGroupByCharacteristic).map((item) => [item]),
          columnBy: "measure",
          rowBy: "",
          ...(settings.index_name && { indexName: settings.index_name.index }),

          originalCurrency: settings.original_currency,
          use_accounting_date: true,
          disableDispatch: true
        }
      ]
    });
  }, [globalQueries]);

  const handleExpandRow = (rowKey: string) => {
    setExpandedRows((prev) => (prev.includes(rowKey) ? prev.filter((key) => key !== rowKey) : [...prev, rowKey]));
  };

  const handleExpandAll = () => {
    if (!tables) return;
    setIsAllExpanded((prev) => {
      if (!prev) {
        // Expand all parent rows
        const rowKeys: string[] = [];

        const collectKeys = (rows: ClientDealTableRow[], parentKey: string = "") => {
          rows.forEach((row: ClientDealTableRow, index: number) => {
            const key = parentKey ? `${parentKey}-${index}` : String(index);
            rowKeys.push(key);

            if (row.children?.length) {
              collectKeys(row.children, key);
            }
          });
        };
        collectKeys(tables.tableData.rows);
        setExpandedRows(rowKeys);
      } else {
        // Collapse all
        setExpandedRows([]);
      }
      return !prev;
    });
  };

  const updateReportData = async (data: DocumentSectionTable["report_data"]) => {
    const { report_data } = (await updateSectionAction({
      id: section.id,
      report_data: data
    })) as DocumentSectionTable;

    const effectiveQueries = report_data?.queries || resultQueries;

    getTableData({
      dateRange: effectiveQueries.dateRange,
      currency: effectiveQueries.currency,
      exchangeMethod: effectiveQueries.exchangeMethod,
      filters: effectiveQueries.filters,

      measures: report_data.settings.columns.measures,
      calculatedFields: report_data.settings.columns.calcFields,
      characteristics: report_data.settings.columns.characteristics,
      columns: syncReportTableColumn(report_data.settings.columns, []),

      columnBy: "measure",
      rowBy: "" as RowBy,
      ...(report_data.settings.index_name && { indexName: report_data.settings.index_name.index }),
      groupBy: report_data.settings.group_by.map(normalizeGroupByCharacteristic).map((item) => [item]),

      originalCurrency: report_data.settings.original_currency,
      use_accounting_date: true,
      disableDispatch: true
    });
  };

  const updateSettings = async () => {
    const updatedSettings = await openPrompt({
      Component: EditTable,
      props: {
        data: settings
      }
    });

    if (!updatedSettings) return;

    updateReportData({
      settings: updatedSettings,
      ...(queries && { queries })
    });
  };

  const updateQueries = async () => {
    const updateQueries = await openPrompt({
      Component: EditQueries,
      props: { data: resultQueries }
    });

    if (!updateQueries) return;

    updateReportData({
      queries: updateQueries,
      settings: settings
    });
  };

  const debouncedOnResize = useCallback(
    debounce((newHeight: number) => {
      if (updateSection) {
        updateSection({
          id: section.id,
          parameters: { height: newHeight }
        });
      }
    }, 500),
    [updateSection, section]
  );

  // Add resize observer to track actual resize changes
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (isResizingRef.current) {
          const newHeight = entry.contentRect.height;
          setTableContainerHeight(newHeight);
          debouncedOnResize(newHeight);
        }
      }
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Handle mouse events for resize tracking
  const handleMouseDown = (e: React.MouseEvent) => {
    // Only track resize if clicking in the bottom right corner (resize handle area)
    const rect = e.currentTarget.getBoundingClientRect();
    const isInBottomRight = e.clientX > rect.right - 20 && e.clientY > rect.bottom - 20;

    if (isInBottomRight) {
      isResizingRef.current = true;
      initialHeightRef.current = tableContainerHeight;
    }
  };

  const handleMouseUp = () => {
    isResizingRef.current = false;
  };

  return (
    <ProgressOverlay
      inProgress={updateSectionLoading || getTableDataLoading}
      containerSX={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        overflow: "hidden",
        borderRadius: "16px",
        gap: "8px",
        padding: "16px",
        boxShadow: "0px 1px 4px 0px #1212171F",
        "&:hover": {
          "& .table-section-menu": {
            opacity: 1
          }
        }
      }}
    >
      <Stack sx={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
        <Typography
          sx={{
            fontSize: "18px",
            fontWeight: 500,
            color: "#071F35"
          }}
        >
          {settings.name}
        </Typography>
        <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
          <Menu
            keepTriggerEnabled
            iconButtonProps={{
              sx: { opacity: 0, transition: "opacity .15s linear" },
              className: "table-section-menu",
              size: "sm"
            }}
            options={[
              {
                title: "Customize Table",
                icon: <CustomizeTable sx={{ width: "16px", height: "16px" }} />,
                onClick: updateSettings
              },
              {
                title: "Parameter Set",
                icon: <ViewWeekOutlinedIcon />,
                onClick: updateQueries
              },
              {
                title: "Delete",
                icon: <BinDeleteIcon />,
                onClick: () =>
                  deleteModal({
                    action: () => deleteSection({ sectionId: section.id })
                  })
              }
            ]}
          />
        </RoleComponentGuard>
      </Stack>
      {queries && <ReportQueries {...queries} />}
      <TableContainer
        ref={tableContainerRef}
        component={Paper}
        sx={{
          maxHeight,
          minHeight: 300,
          height: tableContainerHeight,
          resize: "vertical",
          border: "1px solid #DBE8F5",
          borderRadius: "8px",
          boxShadow: "none",
          position: "relative",
          "&::after": {
            // eslint-disable-next-line quotes
            content: '""',
            position: "absolute",
            bottom: 0,
            right: 0,
            width: "20px",
            height: "20px",
            cursor: "ns-resize",
            backgroundSize: "8px 8px"
          }
        }}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      >
        <Table size="small" stickyHeader>
          <TableHead sx={{ backgroundColor: "rgba(246, 248, 252, 1)" }}>
            <MuiTableRow sx={{ height: "40px" }}>
              <StyledTableHeadCell>
                <IconButton
                  onClick={handleExpandAll}
                  sx={{
                    mr: 1,
                    p: 0,
                    color: "#071F35",
                    transform: isAllExpanded ? "rotate(180deg)" : "none",
                    transition: "transform 0.2s"
                  }}
                >
                  <KeyboardDoubleArrowDownIcon />
                </IconButton>
              </StyledTableHeadCell>
              <StyledTableHeadCell />
              {tables?.tableData.columns.map(({ key, title }) => (
                <StyledTableHeadCell sx={{ maxWidth: 120 }} key={key}>
                  {title}
                </StyledTableHeadCell>
              ))}
            </MuiTableRow>
          </TableHead>
          <TableBody>
            {tables?.tableData.rows.map((row, index) => (
              <TableRow
                key={String(index)}
                row={row}
                rowKey={String(index)}
                level={0}
                expandedRows={expandedRows}
                handleExpandRow={handleExpandRow}
                columns={tables.tableData.columns}
                groupByCounter={settings.group_by.length}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </ProgressOverlay>
  );
};
